import React, { useEffect, useRef } from 'react'
import { noop } from 'lodash'

import {
  getRenderStatus,
  preRenderfarmStatus,
  DragAndDropEvents,
} from '@sketch/modules-common'
import { usePresences } from '../../../presence/operations'
import { AvatarItem } from '../DocumentPresenceCreatorAvatars'

import { thumbnailForPreviewFile, PreviewFile } from '../../utils'
import { DocumentGridItem, DocumentListItem } from './DocumentItem'
import { ModalRestrictor } from '@sketch/components'
import {
  ShareListItemFragment,
  SharesListVersionItemFragment,
} from '@sketch/gql-types'

// ConnectedDocumentItem was identified as a component which is crucial for the
// initial page loading performance. So we are aiming to keep this component optimal.
// One of the thins to make it possible - we want to know the minimal data which
// is necessary for this component to work.
//
// Due to this reason we are picking needed data fields here really granularly.
export interface VersionForDocumentItem
  extends Pick<
    SharesListVersionItemFragment,
    'userIsCreator' | 'updatedAt' | 'createdAt' | 'creator' | 'kind'
  > {
  document: {
    renderStatus: NonNullable<
      SharesListVersionItemFragment['document']
    >['renderStatus']
    previewFile: PreviewFile | null
  } | null
}

export interface ShareForDocumentItem
  extends Pick<
    ShareListItemFragment,
    | 'identifier'
    | 'name'
    | 'sharePrivacyIcon'
    | 'deletedAt'
    | 'updatedAt'
    | 'shareType'
    | 'project'
    | 'userCanUpdateSettings'
  > {
  version: VersionForDocumentItem | null
  archivedAt?: string | null
  pinnedByCurrentUserAt?: string | null
}

export type RenderSharesDropdown<S> = (item: S) => React.ReactNode

export interface ConnectedDocumentItemProps<S> {
  workspaceId: string
  share: S
  hidePresences?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
  renderDropdown: RenderSharesDropdown<S>
  presentation: 'grid' | 'list'
  userIdentifier?: string
  dragAndDropEvents?: DragAndDropEvents
  droppable?: boolean
  selected?: boolean
  onSelectChange?: (selected: boolean) => void

  /* This property only applies to the grid presentation */
  showSelectionCheckbox?: boolean
}

export const ConnectedDocumentItem = <S extends ShareForDocumentItem>(
  props: ConnectedDocumentItemProps<S>
) => {
  const {
    workspaceId,
    share,
    hidePresences,
    presentation,
    onClick,
    userIdentifier,
    renderDropdown,
    dragAndDropEvents,
    droppable,
    selected,
    onSelectChange,
    showSelectionCheckbox,
  } = props

  const canSeePresences = !share?.deletedAt

  const { presences = [] } = usePresences(share.identifier, {
    skip: hidePresences || !canSeePresences,
  })

  const handleItemDismiss = useRef(noop)
  handleItemDismiss.current = selected ? () => onSelectChange?.(false) : noop

  /**
   * If the item is being dismounted (removed) from the view we are preventively
   * unselecting it. This is to avoid having a selected item that is not visible
   * any-more in the UI.
   *
   * This can also occur when a document is moved or deleted and an event removes the
   * items from the listing
   */
  useEffect(() => {
    return () => {
      handleItemDismiss.current()
    }
  }, [])

  const renderStatus = share.version?.document?.renderStatus
  const author = share.version?.creator
  const thumbnailSource = thumbnailForPreviewFile(
    share.version?.document?.previewFile
  )

  const hasPresences = presences.length > 0

  const normalizedRenderStatus = getRenderStatus(
    renderStatus || preRenderfarmStatus(!!thumbnailSource, share.updatedAt!)
  )

  const Component =
    presentation === 'grid' ? DocumentGridItem : DocumentListItem

  const avatars: AvatarItem[] =
    hasPresences && canSeePresences
      ? presences.map(({ user, action }) => ({
          isMe: userIdentifier === user.identifier,
          name: user.name,
          avatar: user.avatarUrl,
          identifier: user.identifier,
          displayName: userIdentifier === user.identifier ? 'You' : user.name,
          action,
        }))
      : [
          {
            isMe: share.version?.userIsCreator ?? false,
            name: author?.name ?? '',
            displayName: share.version?.userIsCreator
              ? 'You'
              : (author?.name ?? ''),
            identifier: author?.identifier!,
            avatar: author?.avatar?.small!,
            action: 'editing',
          },
        ]

  // The "updatedAt" is actually a required field however the schema is wrong and needs to be updated
  // BE issue -> https://github.com/sketch-hq/Cloud/issues/19575
  const lastUpdatedDate = share.version?.updatedAt ?? share.version?.createdAt!

  return (
    <ModalRestrictor>
      <Component
        identifier={share.identifier}
        workspaceId={workspaceId}
        name={share.name}
        thumbnailSource={thumbnailSource}
        renderStatus={normalizedRenderStatus}
        type={share.shareType}
        deletedAt={share.deletedAt}
        documentSharingStatus={share.sharePrivacyIcon}
        lastUpdatedDate={lastUpdatedDate}
        isLatestVersionAutosaved={share.version?.kind === 'AUTO'}
        renderDropdown={() => renderDropdown(share)}
        project={share.project}
        isEditing={hasPresences}
        onClick={onClick}
        avatars={avatars}
        archivedAt={share.archivedAt}
        pinned={share.pinnedByCurrentUserAt}
        dragAndDropEvents={dragAndDropEvents}
        droppable={droppable}
        selected={selected}
        onSelectChange={onSelectChange}
        showSelectionCheckbox={showSelectionCheckbox}
        selectable={share.userCanUpdateSettings}
      />
    </ModalRestrictor>
  )
}
